import React, { useEffect } from 'react';
import DeviceDetection from '../utils/DeviceDetection';
import SmartAppBanner from './SmartAppBanner';
import './MobileIncompatiblePage.css';

/**
 * MobileIncompatiblePage Component
 *
 * Displayed to mobile users to inform them that the web app is not supported
 * on their device and provides links to download the native mobile apps
 */
const MobileIncompatiblePage = ({ currentPath = '/' }) => {
  const deviceType = DeviceDetection.getDeviceType();
  const appUrls = DeviceDetection.getAppStoreUrls();
  const isIPhone = DeviceDetection.isIPhone();
  const isAndroid = DeviceDetection.isAndroid();
  const isFacebookBrowser = DeviceDetection.isFacebookBrowser();
  const isSocialMediaBrowser = DeviceDetection.isSocialMediaBrowser();
  
  // Check if this is a redirect from Facebook or banner
  const urlParams = new URLSearchParams(window.location.search);
  const isFromFacebook = urlParams.get('fromFacebook') === 'true';
  const isFromBanner = urlParams.get('fromBanner') === 'true';
  const shouldOpenApp = urlParams.get('openApp') === 'true';
  const appPath = urlParams.get('appPath') || currentPath; // Use transformed app path if provided
  const isRedirect = isFromFacebook || isFromBanner;
  
  const handleAppDownload = platform => {
    if (platform === 'ios') {
      window.location.href = appUrls.ios;
    } else if (platform === 'android') {
      window.location.href = appUrls.android;
    } else {
      window.location.href = appUrls.fallback;
    }
  };

  // Extract channel ID from URL parameters or current path
  const getChannelId = () => {
    // First check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const channelFromParams = urlParams.get('channel');
    
    if (channelFromParams) {
      return channelFromParams;
    }
    
    // Fallback to extracting from path
    if (currentPath.startsWith('/movie/') || currentPath.startsWith('/series/')) {
      const pathParts = currentPath.split('/');
      return pathParts[pathParts.length - 1]; // Get the channel ID
    }
    return null;
  };
  
  const channelId = getChannelId();
  console.log('🔍 MobileIncompatiblePage: Extracted channel ID:', channelId, 'from URL params or path:', currentPath);

  // Handle opening app with deep link to current content
  const handleOpenAppWithContent = () => {
    // Check if we have the openApp parameter (coming from Facebook redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const shouldOpenApp = urlParams.get('openApp') === 'true';
    
    // If we're in a regular browser but have the openApp parameter, 
    // this means we came from Facebook/banner and should immediately try to open the app
    if (shouldOpenApp && !isFacebookBrowser) {
      console.log('Detected redirect from external source, attempting direct app open');
      console.log('Using app path:', appPath, '(transformed from web path:', currentPath, ')');
      
      if (isIPhone) {
        // iOS: Direct app scheme with channel parameter
        const appScheme = channelId ? `dpro://open?channel=${channelId}` : `dpro://open`;
        console.log('Opening iOS app with deep link from redirect:', appScheme);
        window.location.href = appScheme;
        
        // Shorter fallback timeout since this is a redirect
        setTimeout(() => {
          window.location.href = appUrls.ios;
        }, 1000);
      } else if (isAndroid) {
        // Android: Direct intent URL with transformed path
        const intentUrl = `intent://open${appPath}#Intent;package=com.electric.now;S.browser_fallback_url=${encodeURIComponent(
          appUrls.android,
        )};end`;
        console.log('Opening Android app with deep link from redirect:', intentUrl);
        window.location.href = intentUrl;
      }
      return;
    }
    
    // Special handling for Facebook and social media browsers
    if (isFacebookBrowser) {
      console.log('Facebook browser detected, forcing Safari/Chrome redirect');
      
      if (isIPhone) {
        // iOS: Multiple methods to force Safari opening
        // Use query parameter format that the app expects
        const safariUrl = channelId 
          ? `https://electricnow.tv/?channel=${channelId}&openApp=true&fromFacebook=true`
          : `https://electricnow.tv/?openApp=true&fromFacebook=true`;
        
        // Method 1: Try Safari-specific URL scheme
        try {
          window.location.href = `x-web-search://?${encodeURIComponent(safariUrl)}`;
          console.log('Attempted Safari x-web-search redirect');
        } catch (e) {
          console.log('x-web-search failed, trying alternatives');
        }
        
        // Method 2: Try multiple window.open approaches
        setTimeout(() => {
          try {
            // Try opening with _system target (Cordova-style)
            const newWindow = window.open(safariUrl, '_system');
            if (!newWindow) {
              // If _system fails, try _blank
              window.open(safariUrl, '_blank');
            }
            console.log('Attempted window.open with _system/_blank');
          } catch (e) {
            console.log('window.open failed, trying location redirect');
            // Method 3: Direct location change as last resort
            window.location.href = safariUrl;
          }
        }, 500);
        
        // Method 4: Direct app scheme as final fallback
        setTimeout(() => {
          const appScheme = channelId ? `dpro://open?channel=${channelId}` : `dpro://open`;
          console.log('Final fallback - trying direct app scheme:', appScheme);
          window.location.href = appScheme;
        }, 2000);
        
      } else if (isAndroid) {
        // Android: Force Chrome opening
        // Use query parameter format that the app expects
        const targetUrl = channelId 
          ? `https://electricnow.tv/?channel=${channelId}&openApp=true&fromFacebook=true`
          : `https://electricnow.tv/?openApp=true&fromFacebook=true`;
        
        // Method 1: Try Chrome intent
        const chromeIntent = `intent://${targetUrl.replace('https://', '')}#Intent;scheme=https;package=com.android.chrome;end`;
        try {
          window.location.href = chromeIntent;
          console.log('Attempted Chrome intent redirect');
        } catch (e) {
          console.log('Chrome intent failed, trying alternatives');
        }
        
        // Method 2: Try generic browser intent
        setTimeout(() => {
          const browserIntent = `intent://${targetUrl.replace('https://', '')}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
          try {
            window.location.href = browserIntent;
            console.log('Attempted generic browser intent');
          } catch (e) {
            // Method 3: Direct URL as fallback
            window.location.href = targetUrl;
            console.log('Using direct URL fallback');
          }
        }, 500);
        
        // Method 4: Direct app intent as final fallback
        setTimeout(() => {
          const intentUrl = `intent://open${currentPath}#Intent;package=com.electric.now;S.browser_fallback_url=${encodeURIComponent(
            appUrls.android,
          )};end`;
          console.log('Final fallback - trying direct app intent:', intentUrl);
          window.location.href = intentUrl;
        }, 2000);
      }
    } else {
      // Standard browser handling (Safari, Chrome, etc.)
      if (isIPhone) {
        // iOS: Try custom URL scheme first, fallback to App Store
        const appScheme = channelId ? `dpro://open?channel=${channelId}` : `dpro://open`;
        console.log('Opening iOS app with deep link:', appScheme);
        window.location.href = appScheme;

        // Fallback to App Store if app doesn't open (after 2 seconds)
        setTimeout(() => {
          window.location.href = appUrls.ios;
        }, 2000);
      } else if (isAndroid) {
        // Android: Use intent URL with fallback
        const intentUrl = `intent://open${currentPath}#Intent;package=com.electric.now;S.browser_fallback_url=${encodeURIComponent(
          appUrls.android,
        )};end`;
        console.log('Opening Android app with deep link:', intentUrl);
        window.location.href = intentUrl;
      } else {
        // Fallback for other devices
        handleAppDownload('fallback');
      }
    }
  };

  // Auto-trigger app opening if we're redirected from Facebook
  useEffect(() => {
    if (shouldOpenApp && !isFacebookBrowser) {
      console.log('Auto-triggering app open from Facebook redirect');
      // Small delay to let the page render the "Opening..." message
      setTimeout(() => {
        handleOpenAppWithContent();
      }, 1000);
    }
  }, [shouldOpenApp, isFacebookBrowser, handleOpenAppWithContent]);

  return (
    <div className="mobile-incompatible-page">
      {/* Smart App Banner */}
      <SmartAppBanner currentPath={currentPath} />

      <div className="mobile-incompatible-container">
        {/* ElectricNOW Logo */}
        <div className="mobile-incompatible-logo">
          <img
            src="/assets/images/electric-now-logo.png"
            alt="ElectricNOW TV"
            className="logo-image"
            onError={e => {
              // Fallback to text logo if image fails
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div className="logo-text" style={{ display: 'none' }}>
            <img
              src="/logo192.png"
              alt="ElectricNOW TV"
              className="fallback-logo"
              onError={e => {
                // Final fallback to text
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <h1 className="text-logo" style={{ display: 'none' }}>
              ElectricNOW
            </h1>
          </div>
        </div>

        {/* Main Message */}
        <div className="mobile-incompatible-content">
          <div className="mobile-icon">📱</div>

          <h2 className="mobile-incompatible-title">
            {isRedirect 
              ? 'Opening ElectricNOW App...'
              : isFacebookBrowser 
                ? 'Open in ElectricNOW App' 
                : 'Mobile Web Not Supported'
            }
          </h2>

          <p className="mobile-incompatible-description">
            {isRedirect
              ? `Redirected from ${isFromBanner ? 'app banner' : 'Facebook'}. Attempting to open the ElectricNOW app with your content...`
              : isFacebookBrowser 
                ? 'For the best viewing experience, open this content in the ElectricNOW app.'
                : 'ElectricNOW is optimized for desktop and tablet viewing. For the best mobile experience, please download our native app.'
            }
          </p>

          <div className="device-info">
            <span className="device-detected">
              {isFacebookBrowser 
                ? `Facebook Browser • ${deviceType}` 
                : `Device detected: ${deviceType}`
              }
            </span>
          </div>
          
          {isFacebookBrowser && (
            <div className="facebook-notice">
              <p className="facebook-notice-text">
                🔄 <strong>Attempting to redirect to Safari...</strong>
              </p>
              <p className="facebook-notice-text" style={{ marginTop: '0.5rem', fontSize: '0.8125rem' }}>
                If this doesn't work automatically, tap the menu (⋯) at the bottom of Facebook and select <strong>"Open in Safari"</strong> or <strong>"Open in Browser"</strong>.
              </p>
            </div>
          )}
        </div>

        {/* Primary Action - Open App with Content */}
        <div className="primary-action-section">
          <button
            className="primary-action-button"
            onClick={handleOpenAppWithContent}
          >
            <div className="primary-button-content">
              <div className="primary-button-icon">📱</div>
              <div className="primary-button-text">
                <span className="primary-action-title">
                  {isFacebookBrowser ? 'Open in Safari & Launch App' : 'Open in App'}
                </span>
                <span className="primary-action-subtitle">
                  {isFacebookBrowser ? 'Redirecting to Safari first...' : 'Continue where you left off'}
                </span>
              </div>
            </div>
          </button>
          
          {isFacebookBrowser && (
            <button
              className="secondary-action-button"
              onClick={() => {
                const url = channelId 
                  ? `https://electricnow.tv/?channel=${channelId}`
                  : `https://electricnow.tv/`;
                navigator.clipboard.writeText(url).then(() => {
                  alert('URL copied! Open Safari and paste this link to access the content.');
                }).catch(() => {
                  prompt('Copy this URL and open it in Safari:', url);
                });
              }}
            >
              <div className="secondary-button-content">
                <div className="secondary-button-icon">📋</div>
                <span>Copy Link for Safari</span>
              </div>
            </button>
          )}
        </div>

        {/* App Download Buttons */}
        <div className="app-download-section">
          <h3 className="download-title">Don't have the app yet?</h3>

          <div className="download-buttons">
            {/* iOS App Store Button */}
            {(isIPhone || !isAndroid) && (
              <button
                className="download-button ios-button"
                onClick={() => handleAppDownload('ios')}
              >
                <div className="button-content">
                  <div className="app-store-icon">🍎</div>
                  <div className="button-text">
                    <span className="download-text">Download on the</span>
                    <span className="store-name">App Store</span>
                  </div>
                </div>
              </button>
            )}

            {/* Android Play Store Button */}
            {(isAndroid || !isIPhone) && (
              <button
                className="download-button android-button"
                onClick={() => handleAppDownload('android')}
              >
                <div className="button-content">
                  <div className="app-store-icon">🤖</div>
                  <div className="button-text">
                    <span className="download-text">Get it on</span>
                    <span className="store-name">Google Play</span>
                  </div>
                </div>
              </button>
            )}
          </div>

          {/* Alternative Download Link */}
          <div className="alternative-download">
            <p className="alternative-text">
              Can't find the app?
              <button className="alternative-link" onClick={() => handleAppDownload('fallback')}>
                Visit our download page
              </button>
            </p>
          </div>
        </div>

        {/* Footer Information */}
        <div className="mobile-incompatible-footer">
          <p className="footer-text">
            Stream free movies, TV shows, and live channels on your mobile device with our native
            app.
          </p>

          <div className="footer-links">
            <a href="/privacy-policy" className="footer-link">
              Privacy Policy
            </a>
            <span className="footer-separator">•</span>
            <a href="/terms-of-use" className="footer-link">
              Terms of Use
            </a>
            <span className="footer-separator">•</span>
            <a href="/about" className="footer-link">
              About Us
            </a>
            <span className="footer-separator">•</span>
            <a href="/contact" className="footer-link">
              Contact
            </a>
            <span className="footer-separator">•</span>
            <a href="/do-not-sell" className="footer-link">
              Do Not Sell My Data
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileIncompatiblePage;
